import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DeathConfirmationDetail,
  DeathConfirmationDetailResponse,
  DeathConfirmationState,
  PerUserHistoryEvent,
  PerUserHistoryResponse,
  StopDeliveryResultData,
  SuccessEnvelope,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { CodeChip } from '../components/CodeChip'
import { ConfirmActionDialog } from '../components/ConfirmActionDialog'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { DetailRow } from '../components/DetailRow'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { StatusBadge } from '../components/StatusBadge'
import { useAdminMutation } from '../hooks/useAdminMutation'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'audit', label: 'Audit history' },
  { key: 'manual', label: 'Manual actions' },
] as const
type TabKey = typeof TABS[number]['key']

function nextDeadline(detail: DeathConfirmationDetail): number {
  const candidates = [
    detail.final_warning_deadline,
    detail.current_check_deadline,
    detail.next_life_check_time,
  ].filter((t) => t > 0)
  return candidates.length === 0 ? 0 : Math.min(...candidates)
}

export function UserDetailPage() {
  const params = useParams<{ linkAccount: string }>()
  const linkAccount = params.linkAccount ?? ''
  const client = useAdminApiClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') ?? 'overview') as TabKey

  const detailQuery = useQuery({
    queryKey: queryKeys.userDetail(linkAccount),
    queryFn: () => client.get<DeathConfirmationDetailResponse>(
      `/r/admin/death-confirmations/${linkAccount}`,
    ),
    enabled: !!linkAccount,
  })

  // Cursor-paginated so older state transitions / operator actions
  // remain reachable once a user accrues more than one page of
  // events. (review_comments M3, HEAD 47a2b09)
  const historyQuery = useCursorPaginatedQuery<PerUserHistoryEvent>({
    queryKey: queryKeys.perUserAudit(linkAccount, { limit: 50 }),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: '50' })
      if (cursor) search.set('cursor', cursor)
      return client.get<PerUserHistoryResponse>(
        `/r/admin/death-confirmations/${linkAccount}/events?${search.toString()}`,
      )
    },
    enabled: !!linkAccount,
  })

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 text-sm text-stone-500">
        Loading user…
      </div>
    )
  }
  if (detailQuery.isError || !detailQuery.data?.success) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                   text-red-800"
        role="alert"
      >
        Failed to load <CodeChip>{linkAccount}</CodeChip>.
      </div>
    )
  }

  const detail = detailQuery.data.data

  function changeTab(key: TabKey) {
    const next = new URLSearchParams(searchParams)
    if (key === 'overview') {
      next.delete('tab')
    } else {
      next.set('tab', key)
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            <CodeChip>{detail.link_account}</CodeChip>
            {detail.display_name && (
              <span className="ml-3 text-base font-normal text-stone-700">
                {detail.display_name}
              </span>
            )}
          </h1>
          <Link
            to="/death-confirmation"
            className="text-xs text-accent hover:underline"
          >
            ← Back to queue
          </Link>
        </div>
        <StatusBadge
          kind="death_confirmation"
          value={detail.death_confirmation_state}
        />
      </header>

      <nav
        role="tablist"
        aria-label="User detail tabs"
        className="flex gap-1 border-b border-stone-200"
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            type="button"
            aria-selected={activeTab === tab.key}
            onClick={() => changeTab(tab.key)}
            className={[
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
              activeTab === tab.key
                ? 'border-accent text-accent'
                : 'border-transparent text-stone-600 hover:text-stone-900',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' && (
        <OverviewTab detail={detail} />
      )}
      {activeTab === 'audit' && (
        <AuditTab
          isLoading={historyQuery.isLoading}
          isError={historyQuery.isError}
          rows={historyQuery.flatItems}
          hasMore={historyQuery.hasMore}
          isFetchingMore={historyQuery.isFetchingMore}
          onLoadMore={() => historyQuery.loadMore()}
        />
      )}
      {activeTab === 'manual' && (
        <ManualActionsTab
          linkAccount={linkAccount}
          state={detail.death_confirmation_state}
        />
      )}
    </div>
  )
}

function OverviewTab({ detail }: { detail: DeathConfirmationDetail }) {
  const deadline = nextDeadline(detail)
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
          Identity
        </h2>
        <dl className="mt-2 divide-y divide-stone-100">
          <DetailRow label="Link account">
            <CodeChip>{detail.link_account}</CodeChip>
          </DetailRow>
          {detail.display_name && (
            <DetailRow label="Display name">{detail.display_name}</DetailRow>
          )}
          <DetailRow label="Life check started">
            {detail.start_life_check ? 'Yes' : 'No'}
          </DetailRow>
          <DetailRow label="Last replied">
            <RelativeTime
              timestamp={detail.last_replied}
              emptyLabel="never"
            />
          </DetailRow>
        </dl>
      </section>

      <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
          State
        </h2>
        <dl className="mt-2 divide-y divide-stone-100">
          <DetailRow label="Current state">
            <StatusBadge
              kind="death_confirmation"
              value={detail.death_confirmation_state}
            />
          </DetailRow>
          <DetailRow label="Missed checks">
            {detail.consecutive_missed_checks}
          </DetailRow>
          <DetailRow label="Next deadline">
            {deadline
              ? <RelativeTime timestamp={deadline} />
              : <span className="text-xs text-stone-400">none scheduled</span>}
          </DetailRow>
          <DetailRow label="State updated">
            <RelativeTime
              timestamp={detail.death_confirmation_state_updated}
              emptyLabel="never"
            />
          </DetailRow>
        </dl>
      </section>

      <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
          Notification emails
        </h2>
        <p className="mt-2 text-sm text-stone-700">
          {detail.verified_notification_email_count} verified
        </p>
        <button
          type="button"
          disabled
          title="Available in Wave 2"
          className="mt-3 cursor-not-allowed rounded-md border border-stone-200
                     bg-stone-50 px-3 py-1 text-xs text-stone-500"
        >
          Show notification emails
        </button>
      </section>

      <section
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm
                   lg:col-span-2"
      >
        <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
          Messages ({detail.messages.length})
        </h2>
        <DataTable
          caption={`Messages for ${detail.link_account}`}
          className="mt-2"
          columns={[
            {
              key: 'id',
              header: 'Message ID',
              cell: (m) => <CodeChip>{m.message_id}</CodeChip>,
            },
            {
              key: 'recipient_name',
              header: 'Recipient',
              cell: (m) => m.recipient_name,
            },
            {
              key: 'recipient_domain',
              header: 'Recipient domain',
              cell: (m) => <CodeChip>{m.recipient_email_domain}</CodeChip>,
            },
            {
              key: 'activated',
              header: 'Activated',
              cell: (m) => m.activated ? 'Yes' : 'No',
            },
            {
              key: 'encrypted',
              header: 'Encrypted',
              cell: (m) => m.encrypted ? 'Yes' : 'No',
            },
            {
              key: 'updated',
              header: 'Last modified',
              cell: (m) => <RelativeTime timestamp={m.updated_time}
                                          emptyLabel="never" />,
            },
          ]}
          rows={detail.messages}
          rowKey={(m) => m.message_id}
          emptyTitle="No messages"
        />
      </section>
    </div>
  )
}

interface AuditTabProps {
  isLoading: boolean
  isError: boolean
  rows: PerUserHistoryEvent[]
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}

function AuditTab({
  isLoading, isError, rows, hasMore, isFetchingMore, onLoadMore,
}: AuditTabProps) {
  const columns: ColumnConfig<PerUserHistoryEvent>[] = [
    {
      key: 'time',
      header: 'When',
      cell: (e) => <RelativeTime timestamp={e.time} />,
    },
    {
      key: 'source',
      header: 'Source',
      cell: (e) => <CodeChip>{e.source}</CodeChip>,
    },
    {
      key: 'action',
      header: 'Action',
      cell: (e) => <CodeChip>{e.action}</CodeChip>,
    },
    {
      key: 'transition',
      header: 'Transition',
      cell: (e) =>
        e.from_state || e.to_state
          ? <span className="text-xs text-stone-700">
              {e.from_state || '—'} → {e.to_state || '—'}
            </span>
          : <span className="text-xs text-stone-400">—</span>,
    },
    {
      key: 'reason',
      header: 'Reason / notes',
      cell: (e) => e.reason
        ? <span className="whitespace-pre-wrap text-stone-700">{e.reason}</span>
        : <span className="text-xs text-stone-400">—</span>,
    },
  ]
  return (
    <DataTable
      caption="Per-user audit history"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.event_id}
      isLoading={isLoading}
      isError={isError}
      emptyTitle="No history yet"
      emptyDescription="State transitions and operator actions will appear here as they happen."
      footer={
        <PaginationControl
          loadedCount={rows.length}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          onLoadMore={onLoadMore}
        />
      }
    />
  )
}

interface ResetResponseData {
  death_confirmation_state: DeathConfirmationState | string
}

interface ManualActionsTabProps {
  linkAccount: string
  state: DeathConfirmationState
}

// Friendly per-error-code copy. The dialog falls back to the raw code
// when an unmapped error arrives, so logs and tests stay diagnosable.
const RESET_ERROR_COPY: Record<string, string> = {
  DELIVERING_HAS_PENDING_DELIVERIES:
    'Recipient deliveries are still in flight. Use Stop delivery '
    + 'to revoke pending links, then retry the reset.',
  RESET_REASON_REQUIRED: 'Reason is required (4-500 characters).',
}

const STOP_DELIVERY_ERROR_COPY: Record<string, string> = {
  STOP_DELIVERY_REASON_REQUIRED: 'Reason is required (4-500 characters).',
  USER_NOT_IN_DELIVERING_STATE:
    'User is no longer in DELIVERING — refresh and try again.',
  USER_NOT_FOUND: 'User not found.',
  STOP_DELIVERY_PARTIAL_FAILURE:
    'Some recipient links could not be revoked. Check the audit log '
    + 'and retry; revokes are idempotent.',
}

function ManualActionsTab({ linkAccount, state }: ManualActionsTabProps) {
  const auth = useAuth()
  const canOperate = auth.requireRole('OPERATOR')
  const canAdmin = auth.requireRole('ADMIN')
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [showStopDialog, setShowStopDialog] = useState(false)
  const [lastStopResult, setLastStopResult] =
    useState<StopDeliveryResultData | null>(null)

  // Reset is a single-confirm action: the underlying state machine
  // can re-enter ESCALATING the next cycle, so it isn't strictly
  // irreversible. (Plan 06 reserves "confirm twice" for actions that
  // truly cannot be undone, e.g. delivery revoke / stop delivery.)
  const resetMutation = useAdminMutation<{ reason: string }, ResetResponseData>({
    mutate: (client, { reason }) =>
      client.post<SuccessEnvelope<ResetResponseData>>(
        `/r/admin/death-confirmations/${linkAccount}/reset`,
        { reason },
      ),
    invalidates: [
      queryKeys.userDetail(linkAccount),
      queryKeys.perUserAudit(linkAccount, { limit: 50 }),
      // Invalidate the parent /death-confirmation queue so the row
      // either drops out (no longer flagged) or refreshes its state
      // badge once the operator navigates back. The query key is
      // parameterised by filter chips, so we invalidate the whole
      // 'deathConfirmations' prefix to cover every cached variant.
      // (review_comments M2, HEAD db4990e.)
      ['deathConfirmations'],
      queryKeys.health(),
    ],
  })

  // plan-admin/07: Stop Delivery is a fan-out cascade. The per-record
  // revokes are irreversible at the data-model level, so the dialog
  // uses confirm-twice. Cache invalidation: the user's detail (state
  // is unchanged but revoked metadata may want refresh), the per-user
  // audit history (one parent + N child rows added), the parent queue
  // (the row may drop attention reasons), and health (queue badges).
  const stopMutation = useAdminMutation<
    { reason: string }, StopDeliveryResultData
  >({
    mutate: (client, { reason }) =>
      client.post<SuccessEnvelope<StopDeliveryResultData>>(
        `/r/admin/users/${linkAccount}/stop-delivery`,
        { reason },
      ),
    invalidates: [
      queryKeys.userDetail(linkAccount),
      queryKeys.perUserAudit(linkAccount, { limit: 50 }),
      ['deathConfirmations'],
      queryKeys.health(),
    ],
  })

  const isDelivering = state === 'DELIVERING'
  // Reset is now legal even for DELIVERING — the backend gate (no
  // pending deliveries) is the source of truth and is surfaced inline
  // via DELIVERING_HAS_PENDING_DELIVERIES if the precondition fails.
  // (plan-admin/07.)
  const resetTooltip = isDelivering
    ? 'Reset DELIVERING after Stop delivery has cleared pending links'
    : 'Reset the death-confirmation state machine to ACTIVE'

  let stopTooltip: string
  let stopDisabled: boolean
  if (!canAdmin) {
    stopDisabled = true
    stopTooltip = 'Admin role required to stop delivery for a user'
  } else if (!isDelivering) {
    stopDisabled = true
    stopTooltip = 'Stop delivery is only meaningful for DELIVERING users'
  } else {
    stopDisabled = false
    stopTooltip =
      'Revoke every pending recipient link for this user. Irreversible.'
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-medium uppercase tracking-wide text-stone-600">
        Manual actions
      </h2>
      {!canOperate && (
        <p className="mt-2 text-sm text-stone-600" data-testid="viewer-notice">
          Operator role required to perform manual actions on this user.
          Sign in as an operator (or higher) to enable Reset.
        </p>
      )}
      {canOperate && (
        <>
          <p className="mt-2 text-sm text-stone-600">
            Mutating actions write a row to the per-user audit history
            with the operator id and reason. Force-send and Suppress
            arrive in later waves.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              title={resetTooltip}
              onClick={() => setShowResetDialog(true)}
              className="rounded-md border border-accent bg-white px-3 py-1
                         text-xs font-medium text-accent shadow-sm
                         hover:bg-accent/5"
            >
              Reset death confirmation
            </button>
            <button
              type="button"
              disabled={stopDisabled}
              title={stopTooltip}
              onClick={() => setShowStopDialog(true)}
              className={
                'rounded-md px-3 py-1 text-xs font-medium shadow-sm ' +
                (stopDisabled
                  ? 'cursor-not-allowed border border-stone-200 bg-stone-50 text-stone-500'
                  : 'border border-red-300 bg-white text-red-700 hover:bg-red-50')
              }
            >
              Stop delivery
            </button>
            {(['Force send', 'Suppress'] as const).map((label) => (
              <button
                key={label}
                type="button"
                disabled
                title="Available in Wave 3"
                className="cursor-not-allowed rounded-md border border-stone-200
                           bg-stone-50 px-3 py-1 text-xs text-stone-500"
              >
                {label}
              </button>
            ))}
          </div>
          {lastStopResult && (
            <StopDeliverySummary result={lastStopResult} />
          )}
        </>
      )}

      {showResetDialog && (
        <ConfirmActionDialog
          title="Reset death confirmation"
          body={
            <span>
              Resets the user back to <CodeChip>ACTIVE</CodeChip> and
              clears the missed-cycle counter. This writes a
              {' '}<CodeChip>RESET_DEATH_CONFIRMATION</CodeChip> entry
              to the per-user audit history with the reason.
            </span>
          }
          confirmLabel="Reset"
          errorMessages={RESET_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            await resetMutation.mutateAsync({ reason })
            setShowResetDialog(false)
          }}
          onCancel={() => setShowResetDialog(false)}
        />
      )}

      {showStopDialog && (
        <ConfirmActionDialog
          title="Stop delivery for this user"
          body={
            <span>
              Revokes every pending recipient link for{' '}
              <CodeChip>{linkAccount}</CodeChip>. Each revoke is
              irreversible at the data-model level and cannot be undone.
              The user's death-confirmation state stays{' '}
              <CodeChip>DELIVERING</CodeChip>; reset separately if you
              also need to return them to <CodeChip>ACTIVE</CodeChip>.
            </span>
          }
          confirmLabel="Stop delivery"
          confirmTwice
          errorMessages={STOP_DELIVERY_ERROR_COPY}
          onConfirm={async ({ reason }) => {
            const result = await stopMutation.mutateAsync({ reason })
            setLastStopResult(result)
            setShowStopDialog(false)
          }}
          onCancel={() => setShowStopDialog(false)}
        />
      )}
    </section>
  )
}

function StopDeliverySummary(
  { result }: { result: StopDeliveryResultData },
) {
  const revokedCount = result.revoked_delivery_ids.length
  const skippedCount = result.skipped_delivery_ids.length
  const failedIds = result.failed_delivery_ids ?? []
  const failedCount = failedIds.length
  // plan-admin/07 review H2: partial-failure outcomes get a red
  // panel so operators see the warning at a glance, while the
  // successful revokes are still shown so the cascade isn't
  // collapsed into a single error.
  const isPartial = result.partial_failure === true || failedCount > 0
  const containerClass = isPartial
    ? 'mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900'
    : 'mt-4 rounded-md border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700'
  return (
    <div className={containerClass} data-testid="stop-delivery-summary">
      <div className="font-medium">
        {isPartial && 'Partial failure: '}
        {revokedCount} {revokedCount === 1 ? 'delivery' : 'deliveries'}
        {' '}revoked
        {skippedCount > 0
          ? `, ${skippedCount} already terminal`
          : ''}
        {failedCount > 0
          ? `, ${failedCount} failed`
          : ''}
        .
      </div>
      {revokedCount > 0 && (
        <ul className="mt-2 list-disc pl-5">
          {result.revoked_delivery_ids.map((id) => (
            <li key={id}><CodeChip>{id}</CodeChip></li>
          ))}
        </ul>
      )}
      {failedCount > 0 && (
        <div
          className="mt-2"
          data-testid="stop-delivery-failed-ids"
        >
          <div className="font-medium">Failed (revoke is idempotent — retry safe):</div>
          <ul className="mt-1 list-disc pl-5">
            {failedIds.map((id) => (
              <li key={id}><CodeChip>{id}</CodeChip></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
